<template>
    <div class="ui-page">
        <ui-page-header :title="data ? data.original_name : 'Carregando...'" back="/prospeccao/casos?aba=importacoes" back-text="Importações">
            <template #title-append>
                <ui-badge v-if="data" set="prospect-import" :status="data.status" />
            </template>
            <template #subtitle>
                <span v-if="data">
                    {{ data.source_label }} · enviada em {{ formatDateTime(data.created_at) }}<span v-if="data.uploaded_by"> por {{ data.uploaded_by.name }}</span>
                    <span v-if="data.confirmed_at"> · confirmada em {{ formatDateTime(data.confirmed_at) }}<span v-if="data.confirmed_by"> por {{ data.confirmed_by.name }}</span></span>
                </span>
            </template>
            <v-btn outlined :disabled="!data" @click="downloadFile"><v-icon left small>mdi-download</v-icon>Planilha original</v-btn>
            <template v-if="isPreview">
                <v-btn v-if="$allowed('prospect.import')" outlined class="ui-btn-danger" :disabled="busy" @click="cancel">Cancelar importação</v-btn>
                <v-btn v-if="$allowed('prospect.import')" color="primary" depressed :loading="confirming" :disabled="busy || !data.to_import" @click="confirm">
                    <v-icon left small>mdi-check</v-icon>Confirmar ({{ data.to_import }} {{ data.to_import === 1 ? "caso" : "casos" }})
                </v-btn>
            </template>
        </ui-page-header>

        <section v-if="data" class="pi-stats mb-5" aria-label="Resumo da importação">
            <button v-for="kpi in kpis" :key="kpi.value" type="button" class="pi-stat" :class="{ 'pi-stat--active': tab === kpi.value }" :aria-pressed="String(tab === kpi.value)" @click="tab = kpi.value">
                <span class="pi-stat__label"><span v-if="kpi.tone" class="ui-pill__dot" :class="`ui-pill__dot--${kpi.tone}`"></span>{{ kpi.label }}</span>
                <span class="pi-stat__value">{{ kpi.count }}</span>
                <span class="pi-stat__hint">{{ kpi.hint }}</span>
            </button>
        </section>

        <div v-if="isPreview && reviewPending" class="pi-notice mb-4">
            <v-icon small>mdi-alert-circle-outline</v-icon>
            <span>{{ reviewPending }} {{ reviewPending === 1 ? "linha precisa" : "linhas precisam" }} de revisão: a classe não indica RJ nem TCA. Sem decisão, a linha não entra.</span>
        </div>

        <div v-if="tab === 'discard' && discardGroups.length" class="pi-groups mb-4">
            <span v-for="group in discardGroups" :key="group.reason" class="pi-group"><strong>{{ group.count }}</strong> {{ group.label }}</span>
        </div>

        <ui-table-card clearable :can-clear="!!search" @clear="search = ''">
            <template #toolbar>
                <v-text-field v-model="search" placeholder="Buscar por empresa, processo, vara ou juiz" dense outlined hide-details prepend-inner-icon="mdi-magnify" class="ui-toolbar__search"></v-text-field>
                <span v-if="isPreview && selected.length" class="d-flex align-center" style="gap: 8px;">
                    <span class="ui-small ui-muted">{{ selected.length }} {{ selected.length === 1 ? "selecionada" : "selecionadas" }}</span>
                    <v-btn v-if="$allowed('prospect.import')" small outlined :disabled="busy" @click="decide(selected, 'import')">Importar</v-btn>
                    <v-btn v-if="$allowed('prospect.import')" small outlined :disabled="busy" @click="decide(selected, 'discard')">Descartar</v-btn>
                    <v-btn v-if="$allowed('prospect.import')" small text :disabled="busy" @click="decide(selected, null)">Seguir sugestão</v-btn>
                </span>
            </template>
            <v-data-table v-model="selected" :headers="headers" :items="visibleRows" :loading="loading" disable-sort item-key="id" :show-select="isPreview"
                :item-class="rowClass" :page.sync="page" :items-per-page="itemsPerPage" hide-default-footer show-expand single-expand :expanded.sync="expanded"
                no-data-text="Nenhuma linha neste filtro" loading-text="Carregando...">
                <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
                    <v-simple-checkbox v-if="item.can_decide" :value="isSelected" @input="select($event)"></v-simple-checkbox>
                </template>
                <template v-slot:[`item.company_name`]="{ item }">
                    <div class="ui-cell-main" style="min-width: 230px;">{{ item.company_name || "—" }}</div>
                    <div class="ui-cell-sub ui-mono">{{ item.process_number || "número inválido" }}<span v-if="item.company_document"> · {{ formatDocument(item.company_document) }}</span></div>
                </template>
                <template v-slot:[`item.type`]="{ item }">
                    <v-select v-if="isPreview && item.effective_decision === 'import'" :value="item.type || 'rj'" :items="typeItems" dense outlined hide-details class="pi-type"
                        :menu-props="{ offsetY: true }" :disabled="busy" aria-label="Tipo" @change="(type) => decide([item], 'import', type)"></v-select>
                    <ui-badge v-else-if="item.type" set="prospect-type" :status="item.type" />
                    <span v-else class="ui-muted">—</span>
                </template>
                <template v-slot:[`item.court_name`]="{ item }">
                    <div class="ui-cell-main" style="min-width: 230px;">{{ item.uf || "—" }} · {{ item.court_name || item.comarca || "—" }}</div>
                    <div class="ui-cell-sub">{{ item.judge_name || "juiz não informado" }}</div>
                </template>
                <template v-slot:[`item.claim_amount`]="{ item }"><span class="ui-nowrap">{{ formatMoneyShort(item.claim_amount) }}</span></template>
                <template v-slot:[`item.filed_at`]="{ item }"><span class="ui-nowrap">{{ formatFeedDate(item.filed_at) }}</span></template>
                <template v-slot:[`item.reason_label`]="{ item }">
                    <div class="ui-cell-main">{{ item.reason_label }}</div>
                    <div class="ui-cell-sub" :class="{ 'pi-decision': item.decision }">{{ decisionText(item) }}</div>
                    <div v-if="isPreview && item.suggestion === 'review'" class="pi-row-actions">
                        <template v-if="!item.decision">
                            <v-btn v-if="$allowed('prospect.import')" x-small outlined :disabled="busy" @click="decide([item], 'import')">Importar</v-btn>
                            <v-btn v-if="$allowed('prospect.import')" x-small outlined :disabled="busy" @click="decide([item], 'discard')">Descartar</v-btn>
                        </template>
                        <v-btn v-else-if="$allowed('prospect.import')" x-small text :disabled="busy" @click="decide([item], null)">Desfazer</v-btn>
                    </div>
                    <div v-if="item.operation && item.effective_decision === 'import'" class="pi-operation">
                        <ui-badge set="prospect-operation" :status="item.operation" />
                        <span v-if="item.changes.length" class="ui-cell-sub">{{ fieldList(item.changes) }}</span>
                    </div>
                </template>
                <template v-slot:expanded-item="{ headers: columns, item }">
                    <td :colspan="columns.length" class="pi-expanded">
                        <div class="pi-detail">
                            <div><span class="ui-label">Classe</span>{{ item.process_class || "—" }}</div>
                            <div v-if="item.origin_process_number"><span class="ui-label">Processo originário</span><span class="ui-mono">{{ item.origin_process_number }}</span></div>
                            <div><span class="ui-label">Tribunal · sistema</span>{{ item.tribunal || "—" }}<span v-if="item.system"> · {{ item.system }}</span></div>
                            <div><span class="ui-label">Valor da causa</span>{{ formatMoney(item.claim_amount) }}</div>
                            <div v-if="item.authors.length" class="pi-detail__wide"><span class="ui-label">Autores</span>{{ partiesText(item.authors) }}</div>
                            <div v-if="item.defendants.length" class="pi-detail__wide"><span class="ui-label">Réus</span>{{ partiesText(item.defendants) }}</div>
                            <div v-if="item.summary_ai" class="pi-detail__wide"><span class="ui-label">Resumo do fornecedor</span>{{ item.summary_ai }}</div>
                            <div v-if="item.conflicts.length" class="pi-detail__wide"><span class="ui-label">Conflitos</span>Editado na plataforma, a planilha não sobrescreve: {{ fieldList(item.conflicts) }}.</div>
                            <div v-if="item.files_url_expires_at"><span class="ui-label">Link da íntegra</span>válido até {{ formatDateTime(item.files_url_expires_at) }}</div>
                        </div>
                    </td>
                </template>
            </v-data-table>
            <template #footer>
                <ui-pagination :total="visibleRows.length" :page.sync="page" :items-per-page.sync="itemsPerPage" />
            </template>
        </ui-table-card>
    </div>
</template>

<script>
import axios from "axios";
import {
    FIELD_LABELS, SUGGESTION_TABS, TYPE_ITEMS, formatDateTime, formatDocument, formatFeedDate, formatMoney, formatMoneyShort, notifyError, notifySuccess, openUrl,
} from "@/components/prospect/prospect.util";

export default {
    name: "ProspectImportDetails",

    data: () => ({
        data: null,
        loading: false,
        busy: false,
        confirming: false,
        tab: "relevant",
        search: "",
        selected: [],
        expanded: [],
        page: 1,
        itemsPerPage: 50,
        typeItems: TYPE_ITEMS,
        headers: [
            { text: "Empresa · processo", value: "company_name" },
            { text: "Tipo", value: "type", width: 100 },
            { text: "UF · vara · juiz", value: "court_name" },
            { text: "Valor da causa", value: "claim_amount", align: "end", width: 120 },
            { text: "Distribuição", value: "filed_at", width: 110 },
            { text: "Triagem", value: "reason_label", width: 230 },
            { text: "", value: "data-table-expand", width: 40 },
        ],
    }),

    computed: {
        isPreview() {
            return !!this.data && this.data.status === "preview";
        },
        rows() {
            return this.data ? this.data.rows : [];
        },
        kpis() {
            const count = (value) => (value === "all" ? this.rows.length : this.rows.filter((row) => row.suggestion === value).length);
            const hints = {
                relevant: "RJ e TCA, entram ao confirmar",
                review: "classe genérica, decidir uma a uma",
                discard: "habilitação, incidente, falência, RE",
                all: `${this.data ? this.data.to_import : 0} serão importadas`,
            };
            return SUGGESTION_TABS.map((tab) => ({ value: tab.value, label: tab.text, tone: tab.tone, count: count(tab.value), hint: hints[tab.value] }));
        },
        reviewPending() {
            return this.rows.filter((row) => row.suggestion === "review" && !row.decision).length;
        },
        discardGroups() {
            const groups = {};
            this.rows.filter((row) => row.suggestion === "discard").forEach((row) => {
                groups[row.reason] = groups[row.reason] || { reason: row.reason, label: row.reason_label, count: 0 };
                groups[row.reason].count += 1;
            });
            return Object.values(groups).sort((a, b) => b.count - a.count);
        },
        visibleRows() {
            const term = this.search.trim().toLowerCase();
            return this.rows.filter((row) => {
                if (this.tab !== "all" && row.suggestion !== this.tab) return false;
                if (!term) return true;
                return [row.company_name, row.process_number, row.court_name, row.judge_name, row.comarca].some((value) => String(value || "").toLowerCase().includes(term));
            });
        },
    },

    watch: {
        tab() {
            this.page = 1;
            this.selected = [];
            this.expanded = [];
        },
        search() {
            this.page = 1;
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatDateTime,
        formatDocument,
        formatFeedDate,
        formatMoney,
        formatMoneyShort,

        async load() {
            this.loading = true;
            try {
                const result = await axios.get(`/api/admin/prospect/imports/${this.$route.params.id}`);
                this.data = result.data.content;
                if (this.tab === "relevant" && !this.data.relevant_rows && this.data.review_rows) this.tab = "review";
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        fieldList(fields) {
            return fields.map((field) => FIELD_LABELS[field] || field).join(", ");
        },

        partiesText(parties) {
            return parties.map((party) => (party.document ? `${party.name} (${formatDocument(party.document)})` : party.name)).join("; ");
        },

        decisionText(item) {
            if (item.decision === "import") return "importar (decisão da equipe)";
            if (item.decision === "discard") return "descartar (decisão da equipe)";
            if (item.suggestion === "review") return "aguarda decisão";
            return item.effective_decision === "import" ? "será importado" : "não entra";
        },

        rowClass(item) {
            return item.effective_decision === "import" ? "" : "pi-row--muted";
        },

        async decide(items, decision, type) {
            const rows = items.filter((item) => item.can_decide);
            if (!rows.length) return;
            this.busy = true;
            try {
                const payload = { row_ids: rows.map((row) => row.id), decision };
                if (type) payload.type = type;
                const result = await axios.post(`/api/admin/prospect/imports/${this.data.id}/decisions`, payload);
                this.data = result.data.content;
                this.selected = [];
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = false;
            }
        },

        async confirm() {
            const pending = this.reviewPending ? ` ${this.reviewPending} ${this.reviewPending === 1 ? "linha em revisão sem decisão fica" : "linhas em revisão sem decisão ficam"} de fora.` : "";
            const confirmed = await this.$dialog.confirm({
                title: "Confirmar importação",
                text: `${this.data.to_import} ${this.data.to_import === 1 ? "processo entra" : "processos entram"} na base de prospecção. Casos que já existem são atualizados só nos campos que a equipe não editou.${pending}`,
                actions: { false: "Voltar", true: { text: "Confirmar", color: "primary" } },
            });
            if (!confirmed) return;
            this.busy = true;
            this.confirming = true;
            try {
                const result = await axios.post(`/api/admin/prospect/imports/${this.data.id}/confirm`);
                this.data = result.data.content;
                notifySuccess(this, `Importação confirmada: ${this.data.new_cases} novos, ${this.data.updated_cases} atualizados.`);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = false;
                this.confirming = false;
            }
        },

        async cancel() {
            const confirmed = await this.$dialog.confirm({
                title: "Cancelar importação",
                text: "Nenhum caso é criado ou alterado. A planilha poderá ser enviada de novo depois.",
                actions: { false: "Voltar", true: { text: "Cancelar importação", color: "error" } },
            });
            if (!confirmed) return;
            this.busy = true;
            try {
                const result = await axios.post(`/api/admin/prospect/imports/${this.data.id}/cancel`);
                this.data = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = false;
            }
        },

        async downloadFile() {
            try {
                const result = await axios.get(`/api/admin/prospect/imports/${this.data.id}/file`);
                openUrl(result.data.content.url);
            } catch (error) {
                notifyError(this, error);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.pi-stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border: 1px solid $ui-border;
    border-radius: $ui-radius;
    background: $ui-bg;
    overflow: hidden;

    @media (max-width: 760px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.pi-stat {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 14px 18px;
    text-align: left;
    background: transparent;
    border: 0;
    border-right: 1px solid $ui-border;
    cursor: pointer;

    &:last-child {
        border-right: 0;
    }

    &:hover {
        background: $ui-bg-muted;
    }

    &:focus-visible {
        outline: 2px solid $ui-ring;
        outline-offset: -2px;
    }
}

.pi-stat--active {
    background: $ui-accent-soft;
    box-shadow: inset 0 -2px 0 $ui-accent;
}

.pi-stat__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: $ui-muted;
}

.pi-stat__value {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
    color: $ui-fg;
}

.pi-stat__hint {
    font-size: 12px;
    color: $ui-muted;
}

.pi-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    font-size: 13px;
    border-radius: $ui-radius;
    background: nth(map-get($ui-tones, warning), 1);
    color: nth(map-get($ui-tones, warning), 2);
}

.pi-groups {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.pi-group {
    padding: 4px 10px;
    font-size: 13px;
    border: 1px solid $ui-border;
    border-radius: 999px;
    background: $ui-bg;
    color: $ui-muted;

    strong {
        color: $ui-fg;
    }
}

.pi-row-actions {
    display: flex;
    gap: 6px;
    margin-top: 6px;
}

.pi-operation {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
}

.pi-type {
    max-width: 92px;
}

.pi-decision {
    color: $ui-accent;
}

::v-deep .pi-row--muted td {
    color: $ui-muted;
}

.pi-expanded {
    background: $ui-bg-muted;
}

.pi-detail {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px 24px;
    padding: 14px 4px;
    font-size: 13px;

    .ui-label {
        display: block;
        margin-bottom: 2px;
    }

    @media (max-width: 960px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.pi-detail__wide {
    grid-column: 1 / -1;
}
</style>
